import { Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { addSeconds, isFuture } from 'date-fns';
import { animationFrameScheduler, BehaviorSubject, interval } from 'rxjs';
import { filter, map, share, switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'sbb-flying-focus',
    templateUrl: './flying-focus.component.html',
    styleUrls: ['./flying-focus.component.scss'],
    standalone: false
})
export class FlyingFocusComponent implements OnInit, OnDestroy {
  /**
   * Tab, Enter and Arrow keys
   */
  private readonly allowedKeyCodes = [9, 13, 37, 38, 39, 40];
  private readonly gracePeriodInSeconds = 2;
  private hasBeenFocused = false;
  private lastKeyEvent = new Date();
  private elementSubject = new BehaviorSubject<Element>(undefined);

  constructor(private element: ElementRef, private renderer: Renderer2) {}

  ngOnInit(): void {
    this.elementSubject
      .pipe(
        filter(e => this.isAllowedElement(e)),
        tap(e => this.assignDimension(e)),
        tap(() => this.assignVisibility()),
        share(),
        switchMap(e => interval(0, animationFrameScheduler).pipe(map(() => e)))
      )
      .subscribe(e => this.assignDimension(e));
  }

  ngOnDestroy(): void {
    this.elementSubject.complete();
  }

  @HostListener('document:keydown', ['$event']) onKeydown(event: KeyboardEvent) {
    if (this.allowedKeyCodes.includes(event.which)) {
      this.lastKeyEvent = new Date();
    }
  }

  @HostListener('document:focusin', ['$event.target']) onFocus(element: Element) {
    if (
      isFuture(addSeconds(this.lastKeyEvent, this.gracePeriodInSeconds)) ||
      !this.hasBeenFocused
    ) {
      this.focus(element);
    }
  }

  @HostListener('document:click', ['$event.target']) onClick(element: Element) {
    this.hasBeenFocused = true;
    if (this.elementSubject.getValue() !== element) {
      this.hide();
    }
  }

  @HostListener('window:blur') onBlur() {
    this.hide();
  }

  focus(element: Element) {
    this.elementSubject.next(element);
  }

  private isAllowedElement(element: any) {
    return !!element && element.tagName !== 'sbb-flying-focus';
  }

  private assignDimension(element: Element) {
    const dimension = this.calculateDimension(element);
    for (const key of Object.keys(dimension)) {
      this.renderer.setStyle(this.element.nativeElement, key, `${dimension[key]}px`);
    }
  }

  private calculateDimension(
    element: Element
  ): { left: number; top: number; width: number; height: number } {
    const rect = element.getBoundingClientRect();
    return {
      left: rect.left + window.pageXOffset,
      top: rect.top + window.pageYOffset,
      width: rect.width,
      height: rect.height
    };
  }

  private assignVisibility() {
    if (!this.hasBeenFocused) {
      this.hasBeenFocused = true;
      return;
    }

    this.show();
  }

  private hide() {
    this.renderer.removeAttribute(this.element.nativeElement, 'visible');
  }

  private show() {
    this.renderer.setAttribute(this.element.nativeElement, 'visible', '');
  }
}
